import { asyncTaskResult, asyncTasksListing } from "../../network/network.jsx";
import { ASYNC_TASK_TYPES } from "../constants/OtherKeys.jsx";
import { downloadFileFromData, showToast } from "./utils.jsx";
import { EDITOR_KEYS } from "../constants/EditorKeys.jsx";
import { GRANTS } from "../constants/PrivilegeTypes.jsx";
import { IMPORT_TYPE_NAMES } from "@/other/utils/otherUtils.jsx";

export const processCompletedTask = ({
  alertItem,
  project,
  editorHandler,
  boardManager,
  user,
  isBoardIndependentView,
}) => {
  asyncTasksListing().then((resp) => {
    const result = resp.data;
    let getPollingCompletedTask = {};
    if (!isBoardIndependentView) {
      getPollingCompletedTask = result.filter((pollList) => pollList.id === alertItem.asyncTask)[0];
    } else {
      getPollingCompletedTask = alertItem;
    }

    const entityType = IMPORT_TYPE_NAMES[alertItem.dataType];

    asyncTaskResult(alertItem.asyncTask)
      .then((response) => {
        switch (getPollingCompletedTask.type) {
          case ASYNC_TASK_TYPES.BOARD_DUPLICATION:
            handleBoardDuplication(response, boardManager);
            break;
          case ASYNC_TASK_TYPES.CSV_IMPORT:
            handleCsvImport(response, editorHandler, user, entityType);
            break;
          case ASYNC_TASK_TYPES.CSV_EXPORT:
            handleCsvExport(response, getPollingCompletedTask.meta_data?.file_name);
            break;
          case ASYNC_TASK_TYPES.UNDO_CSV_IMPORT:
            handleUndoCsvImport(response, project);
            break;
          case ASYNC_TASK_TYPES.DEMO_BOARD_CREATION:
            handleDemoBoardCreation(response, boardManager);
            break;
          case ASYNC_TASK_TYPES.BOARD_IMPORT:
            handleBoardImport(response, boardManager);
            break;
          case ASYNC_TASK_TYPES.BOARD_EXPORT:
            handleBoardExport(response);
            break;
          default:
            console.log(`Unknown async task type: ${alertItem.type}`);
        }
      })
      .catch((error) => {
        console.error(
          `Error retrieving result for async task ${alertItem.id}:`,
          error
        );
        showToast({
          msg: `Failed to retrieve result for the completed operation.`,
          type: "error",
          duration: "medium"
        });
      });
  });
};

export const processAutoCompletedTasks = ({ completedTasksPollingList, boardManager }) => {
  /** Filter tasks that should auto-complete and haven't been processed yet */
  const autoCompleteTasks = completedTasksPollingList.filter((task) => {
    return (
      task.type === ASYNC_TASK_TYPES.CSV_EXPORT ||
      task.type === ASYNC_TASK_TYPES.BOARD_EXPORT ||
      task.type === ASYNC_TASK_TYPES.BOARD_DUPLICATION ||
      task.type === ASYNC_TASK_TYPES.DEMO_BOARD_CREATION ||
      task.type === ASYNC_TASK_TYPES.BOARD_IMPORT
    );
  });

  autoCompleteTasks.forEach((task) => {
    asyncTaskResult(task.id)
      .then((response) => {
        switch (task.type) {
          case ASYNC_TASK_TYPES.CSV_EXPORT:
            handleCsvExport(response, task.meta_data?.file_name);
            break;
          case ASYNC_TASK_TYPES.BOARD_EXPORT:
            handleBoardExport(response);
            break;
          case ASYNC_TASK_TYPES.BOARD_DUPLICATION:
          case ASYNC_TASK_TYPES.BOARD_IMPORT:
          case ASYNC_TASK_TYPES.DEMO_BOARD_CREATION:
            handleBoardListingUpdate(response, boardManager);
            break;
          default:
            console.log(`Unknown auto-complete task type: ${task.type}`);
        }
      })
      .catch((error) => {
        console.error(`Error auto-completing task ${task.id}:`, error);
        showToast({
          msg: `Failed to auto-complete operation of async tasks.`,
          type: "error",
          duration: "medium"
        });
      });
  });
};

const handleBoardListingUpdate = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    boardManager.getRefreshUserBoardsListing();
  } else {
    showToast({
      msg: result.message || "Failed to update board listings.",
      type: "error",
      duration: "medium"
    });
  }
}

const handleCsvImport = (response, editorHandler, user, entityType) => {
  const result = response.data;
  const { created_count = 0, updated_count = 0, error_count = 0 } = result;
  const totalImportedRecords = created_count + updated_count + error_count;
  /** totalImportedRecords === 0 - Its means no records were imported, an uploaded file is empty */
  if (totalImportedRecords === 0) {
    showToast({
      msg: "No records were imported. The file may be empty or contain invalid data.",
      type: "warning"
    });
  } else {
    editorHandler.openEditor(EDITOR_KEYS.importEditor, null, {
      importType: entityType.toLowerCase(), canEdit: user && user.can(GRANTS.grant_one), importHeader: entityType, asyncTaskImportResult: result }, { useExisting: true, by: "recordType" }
    )
  }
}


/**
 * Handle CSV export result
 * This method processes the result of a CSV export task.
 * It converts the CSV data from the backend into a Blob object for download.
 * @param response
 * @param fileName
 */
const handleCsvExport = (response, fileName) => {
  const result = response.data;
  if (result) {
    const blob = new Blob([result], { type: 'text/csv;charset=utf-8;' });
    downloadFileFromData(blob, fileName);
    showToast({
      msg: `Successfully exported ${fileName || 'data'} to CSV.`,
      type: "success",
      duration: "medium"
    });
  } else {
    showToast({
      msg: result?.message || "Failed to export to CSV.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle undo CSV import result
 * This method processes the result of an undo CSV import task.
 * The backend will broadcast the changes via WebSocket, so we don't need to reload the project.
 * It also calls the handleToggleContextMenu method to update the history tab content menu of current row.
 * @param response
 * @param project
 */
const handleUndoCsvImport = (response, project) => {
  const result = response.data;
  if (response.status === 200) {
    /** This is here because on async task implementation,
     when the undo is completed, we need to update the context menu and toolbar button states if user do not close the editor,
     so we assign that event here and listen in HistoryTab
     */
    project.importBatchStore.trigger("undoCompletes");
  } else {
    showToast({
      msg: result.message || "Failed to undo CSV import.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle demo board creation result
 * This method processes the result of a demo board creation task and shows a success or error notification.
 * It also refreshes the board list if the operation was successful.
 * @param response
 * @param boardManager
 */
const handleDemoBoardCreation = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    const currentSelectionBoard = boardManager.boardNameStore.query((board) => board.id === result.id, true)[0];
    boardManager.changeSelectedBoard(currentSelectionBoard);
  } else {
    showToast({
      msg: result.message || "Failed to create demo board.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle a board import result
 * This method processes the result of a board import task.
 * It also switches board if the operation was successful.
 * @param response
 * @param boardManager
 */
const handleBoardImport = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    if (typeof result === "object") {
      const currentSelectionBoard = boardManager.boardNameStore.query((board) => board.id === result.id, true)[0]
      boardManager.changeSelectedBoard(currentSelectionBoard)
    }
  } else {
    showToast({
      msg: result.message || "Failed to import board.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle a board export result
 * This method processes the result of a board export task.
 * Download the file also
 * @param response
 */
const handleBoardExport = (response) => {
  const result = response.data;
  if (response.status === 200) {
    // convert blob to json
    const resultJson = new Blob([JSON.stringify(result)], { type: "application/json" });
    downloadFileFromData(resultJson, result.Board[0].display_name, "json");
  } else {
    showToast({
      msg: result.message || "Failed to export board.",
      type: "error",
      duration: "medium"
    });
  }
}

/**
 * Handle a board duplication result
 * It also switching board if the operation was successful.
 * @param response
 * @param boardManager
 */
const handleBoardDuplication = (response, boardManager) => {
  const result = response.data;
  if (response.status === 200) {
    const currentSelectionBoard = boardManager.boardNameStore.query((board) => board.id === result.id, true)[0];
    boardManager.changeSelectedBoard(currentSelectionBoard);
  } else {
    showToast({
      msg: result.message || "Failed to duplicate board.",
      type: "error",
      duration: "medium"
    });
  }
}
